import { ConnectionType } from "../models/Connection";

// ---------------------------------------------------------------------------
// EventKey enum – numeric values must match the backend schema
// ---------------------------------------------------------------------------

export enum EventKey {
  ProjectOpened = 1,
  ProjectCreated = 2,

  ObjectCreated = 3,

  ObjectOpened = 4,

  CsvImported = 5,

  ConnectionCreated = 6,

  GroupAddressesGenerated = 7,

  OpenedProjectCreationAssistent = 8,

  //   ExcelExported = 9, -> is handelt via MenuItemClicked
  //   GiraExported = 10,-> is handelt via MenuItemClicked
  GroupAddressesExported = 11,

  MenuItemClicked = 12,
  ToolbarItemUsed = 13,
  NavigatorSearched = 14,
  ContextMenuItemClicked = 15,
  NavigatorMenuItemClicked = 16,
  ObjectDeleted = 17,
  RoomPlacedInFloorPlan = 18,
  ProductSelected = 19,
  GroupAddressManuallyCreated = 20,
  GroupAddressAssignedToSignal = 21,
  ToastShown = 22,
  BackgroundImageSet = 23,
  QuickstartItemChanged = 24,
  BackgroundPdfUploaded = 25,
  BackgroundImageRemoved = 26,
  NewProductAddedToProject = 27,
  QuickstartPressedForwardButton = 28,
  QuickstartPressedBackButton = 29,
  QuickstartSetName = 30,
  QuickstartAddedPlan = 31,
  QuickstartAddedElements = 32,
  QuickstartDeletedElements = 33,
  QuickstartSelectedManufacturer = 34,
  ProjectSelectionOpened = 35,
  ConceptOneEntryButtonClicked = 36,
  ConceptOneProjectCreationSubmitted = 37,
  ConceptOneProjectCreated = 38,

  // -------------------------------------------------------------------------
  // License/subscription shop (/shop, /selfservice) funnel.
  // -------------------------------------------------------------------------
  ShopOpened = 39,
  ShopPaymentTypeChanged = 40,
  ShopProductSelected = 41,
  ShopCheckoutStarted = 42,
  ShopCheckoutFailed = 43,
  ShopPurchaseCancelled = 44,
  ShopPurchaseSucceededOpened = 45,
  ShopPurchaseVerificationFailed = 46,
  ShopAutoLoginResult = 47,
  ShopProjectSelectionShown = 48,
  ShopProjectSelected = 49,
  ShopPasswordOverlayShown = 50,
  ShopPasswordSet = 51,
  ShopReturnToAppClicked = 52,
  SelfServicePortalOpened = 53,
  SelfServicePortalFailed = 54,
}

// ---------------------------------------------------------------------------
// BaseEvent – shared fields for every event
// ---------------------------------------------------------------------------

export interface BaseEvent {
  ts: number; // seconds since session start (integer)
  e: number; // numeric enum value of the event type (main app or product-specific range)
  elmId?: string; // optional ID of the affected UI element
  d: any; // event-specific data (typed via EventDataMap)
}

// ---------------------------------------------------------------------------
// EventDataMap – strongly-typed payload for each EventKey
// ---------------------------------------------------------------------------

export type EventDataMap = {
  [EventKey.ProjectOpened]: {
    projectId: string;
  };

  [EventKey.ProjectCreated]: {
    projectId: string;
    creationType: "Assistant" | "Manual" | "Duplicated";
  };

  [EventKey.ObjectCreated]: {
    obj: string;
    objectType: string;
  };

  [EventKey.ObjectOpened]: {
    objectType: string;
    obj: string;
  };

  [EventKey.CsvImported]: {
    fileName: string;
    importedRows: number;
  };

  [EventKey.ConnectionCreated]: {
    sourceId: string;
    targetId: string;
    connectionType: ConnectionType;
  };

  [EventKey.GroupAddressesGenerated]: {
    generatedCount: number;
    generationMode: "Manual" | "Automatic";
  };

  [EventKey.OpenedProjectCreationAssistent]: {
    assistantType: "Standard" | "IFC";
  };

  [EventKey.GroupAddressesExported]: {
    format: "xml" | "csv";
    exportedCount: number;
  };

  [EventKey.MenuItemClicked]: {
    menuId: string;
    label?: string;
  };

  [EventKey.ToolbarItemUsed]: {
    toolId: string;
    interaction: "click" | "drag";
  };

  [EventKey.NavigatorSearched]: {
    searchTerm: string;
  };

  [EventKey.ContextMenuItemClicked]: {
    menuId: string;
    contextobj?: string;
  };

  [EventKey.NavigatorMenuItemClicked]: {
    menuId: string;
    contextobj?: string;
  };

  [EventKey.ObjectDeleted]: {
    objectType: string;
    obj: string;
  };

  [EventKey.RoomPlacedInFloorPlan]: {
    roomId: string;
  };

  [EventKey.ProductSelected]: {
    deviceId: string;
    templateId: string;
  };

  [EventKey.GroupAddressManuallyCreated]: {
    gaId: string;
    parentId: string;
  };

  [EventKey.GroupAddressAssignedToSignal]: {
    gaId: string;
    signalId: string;
  };

  [EventKey.ToastShown]: {
    text: string;
    isError: boolean;
  };

  [EventKey.BackgroundImageSet]: {
    pageId: string;
    source: "pdf" | "image";
  };

  [EventKey.QuickstartItemChanged]: {
    step: number;
    action: "added" | "removed";
    itemType: string;
    itemName: string;
  };

  [EventKey.BackgroundPdfUploaded]: {
    pageGroupId: string;
  };

  [EventKey.BackgroundImageRemoved]: {
    pageId: string;
    source: "pdf" | "image";
  };

  [EventKey.NewProductAddedToProject]: {
    productId: string;
    projectId: string;
  };
  [EventKey.QuickstartPressedForwardButton]: {
    currentStep: number;
    finished: boolean;
  };
  [EventKey.QuickstartPressedBackButton]: {
    currentStep: number;
  };
  [EventKey.QuickstartSetName]: {
    name: string;
  };
  [EventKey.QuickstartAddedPlan]: {
    type: "own" | "example_stadtvilla" | "example_Landhaus";
  };
  [EventKey.QuickstartAddedElements]: {
    type: "Floor" | "Room" | "Function";
  };
  [EventKey.QuickstartDeletedElements]: {
    type: "Floor" | "Room" | "Function";
  };
  [EventKey.QuickstartSelectedManufacturer]: {
    manufacturer: string;
  };
  [EventKey.ProjectSelectionOpened]: {
    selection: "estada";
  };
  [EventKey.ConceptOneEntryButtonClicked]: {
    button: "createProject";
  };
  [EventKey.ConceptOneProjectCreationSubmitted]: {
    source: "estadaProjectSelection";
    projectName: string;
  };
  [EventKey.ConceptOneProjectCreated]: {
    source: "estadaProjectSelection";
    projectId: string;
    projectName: string;
  };

  // -------------------------------------------------------------------------
  // License/subscription shop (/shop, /selfservice) funnel.
  // -------------------------------------------------------------------------
  [EventKey.ShopOpened]: {
    entry: "direct" | "inApp"; // "direct" = reached via an external link (e.g. estada.de), without a running app session
    preselectedProduct?: string; // "product" URL parameter, if a product was preselected from the website
  };
  [EventKey.ShopPaymentTypeChanged]: {
    paymentType: "onetime" | "subscription";
  };
  [EventKey.ShopProductSelected]: {
    productId: string;
    itemFamilyId: string;
    paymentType: "onetime" | "subscription";
  };
  [EventKey.ShopCheckoutStarted]: {
    productId?: string;
    priceVariantId: string;
    periodUnit: string;
    price: number;
    auto: boolean; // true if triggered automatically via the "product" URL parameter
  };
  [EventKey.ShopCheckoutFailed]: {
    productId?: string;
    message: string;
  };
  [EventKey.ShopPurchaseCancelled]: Record<string, never>;
  [EventKey.ShopPurchaseSucceededOpened]: {
    itemId: string;
    itemType?: string;
    isSubscription: boolean;
  };
  [EventKey.ShopPurchaseVerificationFailed]: {
    message: string;
  };
  [EventKey.ShopAutoLoginResult]: {
    success: boolean;
    message?: string;
  };
  [EventKey.ShopProjectSelectionShown]: Record<string, never>;
  [EventKey.ShopProjectSelected]: {
    projectId: string;
    isNewProject: boolean;
  };
  [EventKey.ShopPasswordOverlayShown]: {
    newProject: boolean;
  };
  [EventKey.ShopPasswordSet]: {
    success: boolean;
  };
  [EventKey.ShopReturnToAppClicked]: {
    hasProject: boolean;
  };
  [EventKey.SelfServicePortalOpened]: Record<string, never>;
  [EventKey.SelfServicePortalFailed]: {
    message: string;
    hasNoSubscription: boolean;
  };
};
